import { readonly, ref } from 'vue';

/**
 * One shared clock for the whole app.
 *
 * Every countdown and interpolated resource counter reads this, so the UI
 * updates on a single timer instead of one per component, and every number on
 * screen advances on the same frame.
 *
 * The interval starts on import rather than on a `useClock()` call. It used to
 * be started by that call, but the components that matter read `sharedNow`
 * directly and never called it, so nothing ever started the timer and every
 * countdown sat frozen until the page was reloaded. One 250ms interval for the
 * lifetime of the tab is cheap, and it removes a failure mode where reading
 * the clock silently gives you a stale value.
 */
const now = ref(Date.now());

/** Offset between the server's clock and ours, learned from tick payloads. */
const serverOffset = ref(0);

if (typeof window !== 'undefined') {
  window.setInterval(() => {
    now.value = Date.now();
  }, 250);
}

export function useClock() {
  return {
    now: readonly(now),
    /** Our best guess at the server's current time. */
    serverNow: () => now.value + serverOffset.value,
  };
}

/** Called whenever the server tells us its time, to keep countdowns honest. */
export function syncServerTime(serverTime: number): void {
  serverOffset.value = serverTime - Date.now();
}

export function serverNow(): number {
  return Date.now() + serverOffset.value;
}

export { now as sharedNow };
